/**
 * PublicAdmissionKitValidation — public route hit when someone scans
 * the QR code on a signed Kit de Admissão. Fetches metadata from
 * `/api/public/validate-kit/:signature_id` and shows a clean read-only
 * confirmation of the digital signature.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  ShieldCheck, ShieldAlert, Search, Loader2, Copy, CheckCircle2,
  Hash, Calendar, User, BadgeCheck, FileText,
} from "lucide-react";

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
};

const Field = ({ icon: Icon, label, value, mono = false, copyable = false }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    navigator.clipboard.writeText(value || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
      {Icon ? (
        <div className="mt-0.5 w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Icon className="w-3.5 h-3.5 text-slate-600" />
        </div>
      ) : null}
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
          {label}
        </p>
        <p className={`mt-0.5 text-slate-900 break-all ${mono ? "font-mono text-xs" : "text-sm font-medium"}`}>
          {value || "—"}
        </p>
      </div>
      {copyable && value ? (
        <button
          onClick={onCopy}
          className="mt-1 p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700"
          title="Copiar"
        >
          {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  : <Copy className="w-4 h-4" />}
        </button>
      ) : null}
    </div>
  );
};

const PublicAdmissionKitValidation = () => {
  const { signatureId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState(signatureId || "");

  const fetchValidation = useCallback(async (sid) => {
    if (!sid) return;
    setLoading(true);
    setError("");
    setData(null);
    try {
      const res = await axios.get(
        `${API}/public/validate-kit/${encodeURIComponent(sid)}`,
        { timeout: 20000 },
      );
      if (!res.data?.valid) {
        setError("Assinatura não localizada. Verifique se o ID está correto.");
      } else {
        setData(res.data);
      }
    } catch {
      setError("Não foi possível validar agora. Tente novamente em instantes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (signatureId) fetchValidation(signatureId);
  }, [signatureId, fetchValidation]);

  const onSearch = (e) => {
    e?.preventDefault();
    const sid = (searchInput || "").trim().toUpperCase();
    if (!sid) return;
    navigate(`/validar-kit/${encodeURIComponent(sid)}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 px-4 py-10">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold tracking-wider uppercase">
            <BadgeCheck className="w-3.5 h-3.5" /> Validação Pública
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-3">
            {data?.type_label
              ? `${data.type_label} — AVI Segurança`
              : "Validação de Documento Digital — AVI Segurança"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Conferência da assinatura eletrônica via OTP/SMS + ICP-Brasil PAdES
          </p>
        </div>

        <Card className="mb-4">
          <CardContent className="p-4">
            <form onSubmit={onSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-9 font-mono text-sm uppercase"
                  placeholder="AV-2026-05-000001"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  data-testid="kit-validate-input"
                />
              </div>
              <Button type="submit" data-testid="kit-validate-search-btn">
                Verificar
              </Button>
            </form>
          </CardContent>
        </Card>

        {loading && (
          <div className="text-center py-10 text-slate-500">
            <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
            Verificando…
          </div>
        )}

        {error && !loading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-5 flex items-start gap-3">
                <ShieldAlert className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-900">Não validado</p>
                  <p className="text-sm text-red-800 mt-0.5">{error}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {data && !loading && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-emerald-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 pb-3 mb-3 border-b border-emerald-100">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <ShieldCheck className="w-7 h-7 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-700 uppercase tracking-wider">
                      Documento autêntico
                    </p>
                    <p className="text-base text-slate-900 font-bold">
                      {data?.type_label
                        ? `${data.type_label} — assinado digitalmente`
                        : "Documento assinado digitalmente"}
                    </p>
                  </div>
                </div>

                <Field
                  icon={Hash} label="ID Único da Assinatura"
                  value={data.signature_id} mono copyable
                />
                <Field
                  icon={User} label="Funcionário"
                  value={data.nome}
                />
                <Field
                  icon={FileText} label="Matrícula"
                  value={data.matricula} mono
                />
                <Field
                  icon={Calendar} label="Data e hora da assinatura"
                  value={formatDateTime(data.signed_at)}
                />
                {/* Only show Vale-Transporte for legacy admission kits */}
                {data.transport_choice && (
                  <Field
                    icon={BadgeCheck} label="Vale-Transporte"
                    value={data.transport_choice === "SIM"
                      ? "ACEITOU o benefício"
                      : "RECUSOU o benefício"}
                  />
                )}

                <div className="mt-5 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 leading-relaxed">
                  <p className="font-semibold text-slate-700 mb-1">
                    Sobre esta assinatura:
                  </p>
                  Documento eletrônico assinado digitalmente com validade
                  jurídica conforme MP 2.200-2/2001 e Lei 14.063/2020. A
                  assinatura foi confirmada via código OTP enviado por SMS
                  ao número cadastrado do funcionário e protegida com
                  certificado ICP-Brasil PAdES da AVI Serviços de Segurança LTDA.
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        <p className="text-center text-xs text-slate-400 mt-8">
          AVI Segurança — Sistema de assinaturas digitais
        </p>
      </div>
    </div>
  );
};

export default PublicAdmissionKitValidation;
